import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import AdminAuditWidgets from "@/components/admin/AdminAuditWidgets";
import {
  ShieldCheck,
  Plus,
  Trash,
  PencilSimple,
  ChartLineUp,
  Receipt,
  Crown,
  Package,
  Briefcase,
  Truck,
  Storefront,
  Factory,
  ChefHat,
  Wrench,
  MagnifyingGlass,
  SealCheck,
} from "@phosphor-icons/react";

// Keys must match ROLE_AREAS in backend/server.py; what each role may do is
// defined by PERMISSIONS in backend/routes/roles.py.
const INV = "bg-emerald-50 text-emerald-700 border-emerald-200";
const ROLE_META = {
  admin: { label: "Admin", icon: Crown, color: "bg-amber-50 text-amber-700 border-amber-200", desc: "Full access to all workspaces + this admin page" },
  sales: { label: "Sales", icon: ChartLineUp, color: "bg-blue-50 text-blue-700 border-blue-200", desc: "Sales workspace: dashboard, customers, pipeline, orders" },
  finance: { label: "Finance", icon: Receipt, color: "bg-rose-50 text-rose-700 border-rose-200", desc: "Finance workspace: invoices, AR follow-ups, products, settings" },
  // Inventory roles: `edit` / `approve` mirror PERMISSIONS in backend/routes/roles.py.
  inventory: {
    label: "Inventory Admin", icon: Package, color: INV,
    edit: "Everything in Inventory",
    approve: "Everything, including entries they created themselves",
  },
  ceo: {
    label: "CEO", icon: Briefcase, color: INV,
    edit: "Nothing",
    approve: "Internal POs and External (vendor) POs",
  },
  purchase: {
    label: "Purchase Manager", icon: Truck, color: INV,
    edit: "Vendors, items, purchase requisitions, Internal POs, External POs, Stock In, receiving goods (GRN)",
    approve: "Nothing",
  },
  store: {
    label: "Store Keeper", icon: Storefront, color: INV,
    edit: "Items, purchase requisitions, Internal POs, Stock In / Stock Out / Stock Adjust, receiving goods (GRN)",
    approve: "Material requests raised by Production",
  },
  qc: {
    label: "QA", icon: SealCheck, color: INV,
    edit: "NCRs (non-conformance reports), CAPAs",
    approve: "Accept or reject incoming goods in QA inspection",
  },
  production: {
    label: "Production Head", icon: Factory, color: INV,
    edit: "Recipes, production runs, material requests, Internal POs, purchase requisitions, items, CAPAs",
    approve: "Nothing",
  },
  head_chef: {
    label: "Head Chef", icon: ChefHat, color: INV,
    edit: "Recipes, production runs, material requests, Internal POs, purchase requisitions, items, CAPAs",
    approve: "Nothing",
  },
  maintenance_head: {
    label: "Maintenance Head", icon: Wrench, color: INV,
    edit: "Internal POs",
    approve: "Nothing",
  },
  logistics: {
    label: "Logistics", icon: Truck, color: INV,
    edit: "Internal POs",
    approve: "Nothing",
  },
  auditor: {
    label: "Auditor", icon: MagnifyingGlass, color: "bg-slate-50 text-slate-700 border-slate-200",
    edit: "Nothing (view only)",
    approve: "Nothing",
  },
};

const INVENTORY_ROLES = ["inventory", "ceo", "purchase", "store", "qc", "production", "head_chef", "maintenance_head", "logistics", "auditor"];
const ALL_ROLES = ["admin", "sales", "finance", ...INVENTORY_ROLES];
const emptyRoles = () => Object.fromEntries(ALL_ROLES.map((r) => [r, false]));

// Sales and Finance workspaces are granted by the role of the same name; the
// Inventory workspace needs at least one of INVENTORY_ROLES.
const WORKSPACES = [
  { key: "sales", label: "Sales", icon: ChartLineUp, color: ROLE_META.sales.color, desc: "Dashboard, customers, pipeline, orders" },
  { key: "finance", label: "Finance", icon: Receipt, color: ROLE_META.finance.color, desc: "Invoices, AR follow-ups, products, settings" },
  { key: "inventory", label: "Inventory", icon: Package, color: INV, desc: "Items, stock, recipes, POs, production, QA" },
];

function OptionCard({ checked, onChange, icon: Icon, label, desc, details, color, testId }) {
  return (
    <label
      className={
        "flex items-start gap-2.5 px-3 py-2 rounded-md border cursor-pointer transition " +
        (checked ? `${color} border-2` : "bg-white border-slate-200 hover:border-slate-300")
      }
      data-testid={testId}
    >
      <Checkbox className="mt-0.5" checked={checked} onCheckedChange={(v) => onChange(!!v)} />
      <Icon size={16} weight="duotone" className="mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-900">{label}</div>
        {desc && <div className="text-[11px] leading-snug text-slate-500">{desc}</div>}
        {details && (
          <dl className="mt-1 space-y-0.5 text-[11px] leading-snug">
            {details.map(([term, text]) => (
              <div key={term}>
                <dt className="inline font-semibold text-slate-600">{term}: </dt>
                <dd className="inline text-slate-500">{text}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </label>
  );
}

function RoleChip({ role }) {
  const meta = ROLE_META[role];
  if (!meta) return null;
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 border rounded-full text-[11px] font-medium ${meta.color}`}
      data-testid={`chip-${role}`}
    >
      <Icon size={11} weight="bold" />
      {meta.label}
    </span>
  );
}

export default function AdminRoles() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // existing row or null for new
  const [formEmail, setFormEmail] = useState("");
  const [formRoles, setFormRoles] = useState(emptyRoles);
  const [formInventory, setFormInventory] = useState(false);
  const [saving, setSaving] = useState(false);
  const setRole = (role, v) => setFormRoles((p) => ({ ...p, [role]: v }));
  const wsChecked = (key) => (key === "inventory" ? formInventory : !!formRoles[key]);
  const setWs = (key, v) => (key === "inventory" ? setFormInventory(v) : setRole(key, v));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/roles");
      setRows(data || []);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to load users";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  // eslint-disable-next-line
  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditing(null);
    setFormEmail("");
    setFormRoles(emptyRoles());
    setFormInventory(false);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setFormEmail(row.email);
    // Keep every role the user has — including any not shown here — so saving never drops one.
    setFormRoles({ ...emptyRoles(), ...Object.fromEntries(row.roles.map((r) => [r, true])) });
    setFormInventory(row.roles.some((r) => INVENTORY_ROLES.includes(r)));
    setDialogOpen(true);
  };

  const save = async () => {
    const email = formEmail.trim().toLowerCase();
    if (!email.includes("@")) {
      toast.error("Enter a valid email address");
      return;
    }
    if (!formRoles.admin && !WORKSPACES.some((w) => wsChecked(w.key))) {
      toast.error("Select at least one workspace");
      return;
    }
    if (formInventory && !INVENTORY_ROLES.some((r) => formRoles[r])) {
      toast.error("Select at least one Inventory role");
      return;
    }
    // Inventory roles only count while the Inventory workspace is selected.
    const roles = Object.entries(formRoles)
      .filter(([k, v]) => v && (formInventory || !INVENTORY_ROLES.includes(k)))
      .map(([k]) => k);
    setSaving(true);
    try {
      await api.post("/admin/roles", { email, roles });
      toast.success(`Saved roles for ${email}`);
      setDialogOpen(false);
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to save";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (row.email === (user?.email || "").toLowerCase()) {
      toast.error("You cannot remove your own admin access.");
      return;
    }
    if (!window.confirm(`Remove all access for ${row.email}?\nThey will lose access the next time they load the app.`)) return;
    try {
      await api.delete(`/admin/roles/${encodeURIComponent(row.email)}`);
      toast.success(`Removed ${row.email}`);
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to remove";
      toast.error(msg);
    }
  };

  const stats = useMemo(() => {
    const s = { total: rows.length, admin: 0, sales: 0, finance: 0, inventory: 0 };
    rows.forEach((r) => {
      r.roles.forEach((role) => { if (role in s && role !== "inventory") s[role] += 1; });
      if (r.roles.some((role) => INVENTORY_ROLES.includes(role))) s.inventory += 1;
    });
    return s;
  }, [rows]);

  return (
    <div data-testid="admin-roles-page">
      <div className="px-8 pt-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-heading text-xl font-bold tracking-tight text-slate-900">
              User Management
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Manage who can access the Sales, Finance and Inventory workspaces.
            </p>
          </div>
          <button
            onClick={openAdd}
            className="inline-flex items-center gap-2 px-4 h-10 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
            data-testid="add-user-btn"
          >
            <Plus size={14} weight="bold" /> Add user
          </button>
        </div>
      </div>

      <div className="px-8 pt-5 pb-8 space-y-5">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-5">
          {[
            { label: "Total users", value: stats.total, accent: "bg-slate-50 text-slate-700", icon: ShieldCheck },
            { label: "Admins", value: stats.admin, accent: "bg-amber-50 text-amber-700", icon: Crown },
            { label: "Sales", value: stats.sales, accent: "bg-blue-50 text-blue-700", icon: ChartLineUp },
            { label: "Finance", value: stats.finance, accent: "bg-rose-50 text-rose-700", icon: Receipt },
            { label: "Inventory users", value: stats.inventory, accent: "bg-emerald-50 text-emerald-700", icon: Package },
          ].map((s) => (
            <div key={s.label} className="bg-white border border-slate-200 rounded-lg p-5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">{s.label}</div>
                  <div className="mt-2 font-heading text-2xl font-bold text-slate-900">{s.value}</div>
                </div>
                <div className={`w-9 h-9 rounded-md flex items-center justify-center ${s.accent}`}>
                  <s.icon size={18} weight="duotone" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Recent role changes + Active users */}
        <AdminAuditWidgets />

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="roles-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                <th className="px-6 py-3">Email</th>
                <th className="px-6 py-3">Roles</th>
                <th className="px-6 py-3">Updated</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan={4} className="px-6 py-10 text-center text-slate-500">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={4} className="px-6 py-12 text-center text-slate-500">No users yet. Click &quot;Add user&quot; to grant access.</td></tr>
              )}
              {!loading && rows.map((r) => {
                const isMe = r.email === (user?.email || "").toLowerCase();
                return (
                  <tr key={r.email} className="hover:bg-slate-50" data-testid={`role-row-${r.email}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-900">{r.email}</span>
                      </div>
                      {isMe && (
                        <div className="text-[10px] uppercase tracking-[0.16em] text-amber-600 font-semibold mt-0.5">
                          That&apos;s you
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1.5">
                        {r.roles.map((role) => <RoleChip key={role} role={role} />)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-500 text-xs">
                      <div>{new Date(r.updated_at).toLocaleString()}</div>
                      <div className="text-slate-400">by {r.updated_by}</div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="inline-flex items-center gap-2">
                        <button
                          onClick={() => openEdit(r)}
                          className="inline-flex items-center gap-1.5 px-2.5 h-8 rounded-md border border-slate-200 bg-white text-slate-700 text-xs font-medium hover:bg-slate-50"
                          data-testid={`edit-${r.email}`}
                        >
                          <PencilSimple size={12} weight="bold" /> Edit
                        </button>
                        <button
                          onClick={() => remove(r)}
                          disabled={isMe}
                          className="inline-flex items-center gap-1.5 px-2.5 h-8 rounded-md border border-rose-200 bg-rose-50 text-rose-700 text-xs font-medium hover:bg-rose-100 disabled:opacity-40 disabled:cursor-not-allowed"
                          data-testid={`remove-${r.email}`}
                          title={isMe ? "You cannot remove your own admin access" : "Remove user"}
                        >
                          <Trash size={12} weight="bold" /> Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="role-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit roles for ${editing.email}` : "Add user to workspace"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Email</Label>
              <Input
                value={formEmail}
                onChange={(e) => setFormEmail(e.target.value)}
                disabled={!!editing}
                placeholder="user@svraf.com"
                className="mt-1.5"
                data-testid="form-email"
              />
              {!editing && (
                <p className="text-xs text-slate-500 mt-1.5">
                  Must match the Google account they sign in with.
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Administrator</Label>
              <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <OptionCard
                  checked={!!formRoles.admin}
                  onChange={(v) => setRole("admin", v)}
                  icon={ROLE_META.admin.icon}
                  label={ROLE_META.admin.label}
                  desc={ROLE_META.admin.desc}
                  color={ROLE_META.admin.color}
                  testId="role-toggle-admin"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">
                Workspaces {!formRoles.admin && <span className="text-rose-600">*</span>}
              </Label>
              <p className="text-xs text-slate-500 mt-1">
                {formRoles.admin
                  ? "Admins already open every workspace."
                  : "Select at least one. The user lands in this workspace after login."}
              </p>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
                {WORKSPACES.map((w) => (
                  <OptionCard
                    key={w.key}
                    checked={wsChecked(w.key)}
                    onChange={(v) => setWs(w.key, v)}
                    icon={w.icon}
                    label={w.label}
                    desc={w.desc}
                    color={w.color}
                    testId={`workspace-toggle-${w.key}`}
                  />
                ))}
              </div>
            </div>
            {formInventory && (
              <div data-testid="inventory-roles">
                <Label className="text-xs uppercase tracking-wider text-slate-500">
                  Inventory roles <span className="text-rose-600">*</span>
                </Label>
                <p className="text-xs text-slate-500 mt-1">
                  Select at least one. Every Inventory role can <b>view</b> all Inventory records; the role decides what
                  they can <b>create / edit</b> and <b>approve</b>. Nobody can approve an entry they created themselves
                  (except Inventory Admin and Admin).
                </p>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {INVENTORY_ROLES.map((role) => {
                    const meta = ROLE_META[role];
                    return (
                      <OptionCard
                        key={role}
                        checked={!!formRoles[role]}
                        onChange={(v) => setRole(role, v)}
                        icon={meta.icon}
                        label={meta.label}
                        details={[["Create / Edit", meta.edit], ["Approve", meta.approve]]}
                        color={meta.color}
                        testId={`role-toggle-${role}`}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <button
              onClick={() => setDialogOpen(false)}
              className="px-4 h-10 rounded-md border border-slate-200 bg-white text-slate-700 text-sm font-medium"
              data-testid="cancel-btn"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="px-4 h-10 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50"
              data-testid="save-role-btn"
            >
              {saving ? "Saving…" : editing ? "Save changes" : "Add user"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
